import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Eye, ArrowRight, ListChecks, Settings as SettingsIcon, LogOut, Sparkles, Gauge } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const SAMPLE_PROMPTS = [
  {
    id: "boundary",
    label: "Set a boundary",
    goal: "Set a boundary",
    recipient: "A friend who keeps asking to borrow money",
    message:
      "Hey, I&apos;ve been thinking and I can&apos;t keep lending money — it&apos;s starting to stress me out and change how I feel about hanging out. I still want to see you, just not around money stuff.",
  },
  {
    id: "apology",
    label: "Apologize to a coworker",
    goal: "Apologize",
    recipient: "A coworker I snapped at in a meeting",
    message:
      "I owe you an apology for how I came across in the meeting today. I was frustrated about the deadline and it landed on you and that wasn&apos;t fair. I&apos;ll be more mindful next time.",
  },
];

const statusStyles = {
  pending: "text-muted-foreground",
  sent: "text-[hsl(var(--success))]",
  edited: "text-[hsl(var(--warning))]",
  cancelled: "text-muted-foreground",
};

// Decode HTML entities like &apos; back to real chars for the composer prefill
const decode = (s) => s.replace(/&apos;/g, "'").replace(/&amp;/g, "&").replace(/&quot;/g, '"');

export default function Dashboard() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [reviewsRes, quotaRes] = await Promise.all([
          api.get("/reviews"),
          api.get("/quota").catch(() => ({ data: null })),
        ]);
        setReviews(reviewsRes.data);
        setQuota(quotaRes.data);
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const recent = reviews.slice(0, 4);

  const trySample = (s) => {
    nav("/app/compose", {
      state: { prefill: decode(s.message), recipient: s.recipient, goal: s.goal },
    });
  };

  return (
    <div data-testid="dashboard-screen">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Welcome back</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight" data-testid="dashboard-email">{user?.email}</h1>
        </div>
        <button
          onClick={signOut}
          className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary md:hidden"
          data-testid="dashboard-signout-mobile"
          aria-label="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>

      {/* Primary CTA */}
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 mb-8" data-testid="dashboard-primary-cta">
        <div className="flex items-start gap-4 mb-6">
          <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <Eye className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight mb-1">Start a FINALLOOK</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Paste a message you&apos;re about to send. We&apos;ll show you how it may come across and suggest ways to sharpen it.
            </p>
          </div>
        </div>
        {quota && (
          <div className="flex items-center gap-2 mb-5 text-xs text-muted-foreground" data-testid="dashboard-quota">
            <Gauge className="h-3.5 w-3.5" />
            {quota.limit === null ? (
              <span>Unlimited reviews on the {quota.plan} plan</span>
            ) : (
              <span>
                <span className="font-semibold text-foreground" data-testid="dashboard-quota-used">{quota.used_today}</span>
                <span> / {quota.limit} reviews today · {quota.plan} plan</span>
              </span>
            )}
          </div>
        )}
        <Link to="/app/compose">
          <Button
            className="w-full sm:w-auto rounded-full h-12 px-8 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold disabled:opacity-50"
            disabled={quota && quota.limit !== null && quota.remaining === 0}
            data-testid="dashboard-start-btn"
          >
            {quota && quota.limit !== null && quota.remaining === 0 ? "Daily limit reached" : "New review"}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>

      {/* Recent Reviews */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold tracking-tight">Recent reviews</h3>
          <Link to="/app/reviews" className="text-sm text-primary font-medium hover:underline" data-testid="dashboard-view-all-reviews">
            View all
          </Link>
        </div>

        {loading ? (
          <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground" data-testid="dashboard-reviews-loading">
            Loading…
          </div>
        ) : recent.length === 0 ? (
          <div className="space-y-4" data-testid="dashboard-reviews-empty">
            <div className="rounded-xl border border-dashed border-border bg-card/50 p-6 text-sm text-muted-foreground">
              No reviews yet. Try one of these samples to see FINALLOOK in action.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SAMPLE_PROMPTS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => trySample(s)}
                  className="text-left rounded-xl border border-border bg-card p-4 hover:border-primary/50 hover:bg-secondary/40 group"
                  data-testid={`dashboard-sample-${s.id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs uppercase tracking-[0.15em] text-primary font-semibold">{s.label}</span>
                  </div>
                  <p className="text-sm text-foreground/90 leading-relaxed line-clamp-3">
                    {decode(s.message)}
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground group-hover:text-foreground">
                    Try this <ArrowRight className="h-3 w-3" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="dashboard-recent-list">
            {recent.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/app/reviews/${r.id}`}
                  className="block rounded-xl border border-border bg-card px-4 py-4 hover:bg-secondary/40"
                  data-testid={`dashboard-review-item-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-sm text-foreground line-clamp-2 flex-1">{r.message_preview}</p>
                    <span className="font-heading font-bold text-lg text-primary shrink-0">{r.overall_score}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <span>{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</span>
                    <span>·</span>
                    <span className={statusStyles[r.status] || ""}>{r.status}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Secondary links */}
      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/app/reviews"
          className="rounded-xl border border-border bg-card p-4 hover:bg-secondary/40 flex items-center gap-3"
          data-testid="dashboard-reviews-link"
        >
          <ListChecks className="h-5 w-5 text-primary" />
          <span className="text-sm font-medium">Review history</span>
        </Link>
        <Link
          to="/app/settings"
          className="rounded-xl border border-border bg-card p-4 hover:bg-secondary/40 flex items-center gap-3"
          data-testid="dashboard-settings-link"
        >
          <SettingsIcon className="h-5 w-5 text-primary" />
          <span className="text-sm font-medium">Settings</span>
        </Link>
      </div>
    </div>
  );
}
